import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { JwtDecoder } from '../components/JwtDecoder.jsx';
import { SaveIcon, AddIcon, RemoveIcon, KeyIcon } from '../components/icons';

/**
 * Per-project end-user identity config.
 *
 * Defaults inherit from the tenant row — blank = "fall back to
 * tenant".  When you need a project on a separate IdP from the
 * rest of the tenant (different audience claim, different JWKS
 * endpoint, different admin permission name), set the override
 * here and the SDK edge validates tokens against it instead.
 *
 * Admin strategy picks HOW the backend recognises admins on the
 * end-user side:
 *   • table_lookup — Spotlight.Admin.Users row (default).
 *   • jwt_permission — JWT carries a named permission in its
 *     `permissions` array (e.g. "spotlight:admin").
 *   • jwt_role — JWT's `role` (or `roles` list) contains a value.
 */

const STRATEGIES = [
  { id: '', label: 'Inherit from tenant' },
  { id: 'table_lookup', label: 'Admin.Users table lookup (default)' },
  { id: 'jwt_permission', label: 'JWT permission scope' },
  { id: 'jwt_role', label: 'JWT role claim' },
];

export function ProjectAuth() {
  const { getToken } = useAuth();
  const { project } = useOutletContext() || {};
  const projectId = project?.project_id;
  const api = useMemo(
    () => createApiClient(getToken, { projectId }),
    [getToken, projectId],
  );

  const [current, setCurrent] = useState(null);
  const [tenantFallback, setTenantFallback] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Form state
  const [jwksUrl, setJwksUrl] = useState('');
  const [issuer, setIssuer] = useState('');
  const [audience, setAudience] = useState('');
  const [strategy, setStrategy] = useState('');
  const [permission, setPermission] = useState('');

  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersErr, setAdminUsersErr] = useState(null);

  // JWT decoder modal — browser-only debugger for operator tokens.
  const [decoderOpen, setDecoderOpen] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [p, cfg, admins] = await Promise.all([
        api(`/v1/admin/projects/${encodeURIComponent(projectId)}`),
        api('/v1/admin/config').catch(() => ({})),
        api('/v1/admin/users').catch(() => ({ items: [] })),
      ]);
      setCurrent(p);
      setJwksUrl(p?.jwks_url || '');
      setIssuer(p?.jwt_issuer || '');
      setAudience(p?.jwt_audience || '');
      setStrategy(p?.admin_strategy || '');
      setPermission(p?.admin_permission || '');
      setTenantFallback({
        jwks_url: cfg?.jwks_url || '',
        jwt_issuer: cfg?.jwt_issuer || '',
        jwt_audience: cfg?.jwt_audience || '',
        admin_strategy: cfg?.admin_strategy || 'table_lookup',
        platform_admin_permission: cfg?.platform_admin_permission || 'spotlight:admin',
      });
      const list = Array.isArray(admins) ? admins : (admins?.items || []);
      setAdminUsers(list);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, projectId]);

  useEffect(() => { load(); }, [load]);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api(`/v1/admin/projects/${encodeURIComponent(projectId)}`, {
        method: 'PATCH',
        body: JSON.stringify({
          jwks_url: jwksUrl.trim(),
          jwt_issuer: issuer.trim(),
          jwt_audience: audience.trim(),
          admin_strategy: strategy,
          admin_permission: permission.trim(),
        }),
      });
      setNotice('Saved. SDK picks up the change within ~30s (tenant cache TTL).');
      await load();
    } catch (ex) {
      setError(ex.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="stack" style={{ marginTop: 4 }}>
      <div>
        <h2 style={{ margin: '0 0 4px' }}>End-user auth</h2>
        <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13.5 }}>
          How the SDK edge recognises users on your customer pages —
          which IdP's tokens are valid, and which of those users are
          admins. Anything you leave blank here inherits from the
          tenant row.
        </p>
      </div>

      {loading && (
        <div className="state"><div className="state__title">Loading…</div></div>
      )}
      {error && !loading && (
        <div className="state state--error">
          <div className="state__title">Couldn't load auth config</div>
          <div>{error}</div>
        </div>
      )}
      {notice && <div className="state"><div>{notice}</div></div>}

      {!loading && !error && (
        <>
          <form onSubmit={save} className="card">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <h3 className="card__title" style={{ margin: 0 }}>Identity provider (JWKS)</h3>
              <button
                type="button"
                className="btn"
                onClick={() => setDecoderOpen(true)}
                title="Paste a JWT and inspect its claims locally — nothing leaves your browser."
              >
                <KeyIcon /> Decode a token
              </button>
            </div>
            <p className="card__body" style={{ marginTop: 8 }}>
              Pages scoped to this project forward end-user tokens
              that validate against this JWKS. Leave blank to fall
              back to the tenant's setting.
            </p>
            <div className="stack" style={{ marginTop: 10, gap: 10 }}>
              <InheritField
                label="JWKS URL"
                fallback={tenantFallback.jwks_url}
                value={jwksUrl}
                onChange={setJwksUrl}
                placeholder="https://your-idp.example.com/.well-known/jwks.json"
              />
              <InheritField
                label="Issuer (iss claim must match)"
                fallback={tenantFallback.jwt_issuer}
                value={issuer}
                onChange={setIssuer}
                placeholder="https://your-idp.example.com"
              />
              <InheritField
                label="Audience (aud claim must match)"
                fallback={tenantFallback.jwt_audience}
                value={audience}
                onChange={setAudience}
                placeholder="(leave blank to skip audience check)"
              />
            </div>

            <h3 className="card__title" style={{ marginTop: 20 }}>Admin detection</h3>
            <p className="card__body">
              How the SDK decides a user is an admin on this project.
              Table lookup is the default — it's a row in
              <code> Spotlight.Admin.Users </code>
              keyed by the user's id from the JWT's <code>sub</code>
              claim. JWT-permission + JWT-role avoid that round-trip
              when your IdP already carries the info.
            </p>
            <div className="stack" style={{ marginTop: 10, gap: 10 }}>
              <label className="field">
                <span className="field__label">Strategy</span>
                <select
                  className="select"
                  value={strategy}
                  onChange={(e) => setStrategy(e.target.value)}
                >
                  {STRATEGIES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}{s.id === '' ? ` · inherits ${tenantFallback.admin_strategy}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              {(strategy === 'jwt_permission' || strategy === 'jwt_role') && (
                <InheritField
                  label={strategy === 'jwt_permission' ? 'Permission scope required' : 'Role claim value required'}
                  fallback={tenantFallback.platform_admin_permission}
                  value={permission}
                  onChange={setPermission}
                  placeholder={strategy === 'jwt_permission' ? 'spotlight:admin' : 'admin'}
                />
              )}
            </div>

            {error && <p style={{ color: 'var(--danger-700)', marginTop: 10 }}>{error}</p>}
            <div className="row" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
              <button type="submit" className="btn btn--primary" disabled={saving}>
                <SaveIcon /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>

          <section className="card">
            <h3 className="card__title">Admin users (table lookup)</h3>
            <p className="card__body">
              Who counts as an admin on the end-user side when the
              strategy is <code>table_lookup</code>. Add a row per
              person who should see the Spotlight overlay on your
              pages; JWT-permission strategies don't need entries
              here.
            </p>
            <AddAdminUserForm api={api} onAdded={load} />
            {adminUsersErr && (
              <div className="state state--error" style={{ marginTop: 10 }}>
                <div className="state__title">Couldn't load admin users</div>
                <div>{adminUsersErr}</div>
              </div>
            )}
            {!adminUsersErr && adminUsers.length === 0 && (
              <div className="list__meta" style={{ marginTop: 10 }}>
                No Admin.Users rows on this tenant yet. Add one above.
              </div>
            )}
            {!adminUsersErr && adminUsers.length > 0 && (
              <ul className="list" style={{ marginTop: 10 }}>
                {adminUsers.map((u) => (
                  <AdminUserRow
                    key={u.user_id}
                    user={u}
                    api={api}
                    onRemoved={load}
                  />
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      <JwtDecoder
        open={decoderOpen}
        onClose={() => setDecoderOpen(false)}
        jwksUrl={jwksUrl || tenantFallback.jwks_url || ''}
        issuer={issuer || tenantFallback.jwt_issuer || ''}
        audience={audience || tenantFallback.jwt_audience || ''}
      />
    </section>
  );
}

function AddAdminUserForm({ api, onAdded }) {
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('admin');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api('/v1/admin/users', {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId.trim(),
          email: email.trim(),
          display_name: displayName.trim(),
          role,
        }),
      });
      setUserId('');
      setEmail('');
      setDisplayName('');
      setRole('admin');
      onAdded();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'grid',
        gridTemplateColumns: '2fr 2fr 2fr 1fr auto',
        gap: 10,
        alignItems: 'end',
        marginTop: 12,
      }}
    >
      <label className="field">
        <span className="field__label">User id (JWT <code>sub</code>)</span>
        <input
          className="input"
          required
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="auth0|68a…"
        />
      </label>
      <label className="field">
        <span className="field__label">Email</span>
        <input
          className="input"
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jane@acme.com"
        />
      </label>
      <label className="field">
        <span className="field__label">Display name</span>
        <input
          className="input"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Jane Doe"
        />
      </label>
      <label className="field">
        <span className="field__label">Role</span>
        <select className="select" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="owner">Owner</option>
          <option value="admin">Admin</option>
          <option value="editor">Editor</option>
          <option value="viewer">Viewer</option>
        </select>
      </label>
      <button type="submit" className="btn btn--primary" disabled={busy || !userId || !email}>
        <AddIcon /> {busy ? 'Adding…' : 'Add'}
      </button>
      {err && (
        <p style={{ color: 'var(--danger-700)', gridColumn: '1 / -1', margin: 0 }}>
          {err}
        </p>
      )}
    </form>
  );
}

function AdminUserRow({ user, api, onRemoved }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmNode, ask] = useConfirm();

  function remove() {
    ask({
      title: `Remove ${user.display_name || user.email || user.user_id} from the admin table?`,
      body: 'End-user pages stop treating them as admin on next request.',
      confirmLabel: 'Remove',
      danger: true,
      runOnConfirm: async () => {
        setBusy(true);
        setErr(null);
        try {
          await api(`/v1/admin/users/${encodeURIComponent(user.user_id)}`, {
            method: 'DELETE',
          });
          onRemoved();
        } catch (ex) {
          setErr(ex.message);
          setBusy(false);
          throw ex;
        }
      },
    });
  }

  return (
    <li className="list__row">
      <div className="list__primary">
        <span className="list__name">
          {user.display_name || user.email || user.user_id}
          {!user.is_active && (
            <span className="badge badge--default" style={{ marginLeft: 8 }}>Inactive</span>
          )}
        </span>
        <span className="list__meta">
          <code>{user.user_id}</code>
          {user.email && <> · {user.email}</>}
        </span>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <span className={`badge badge--${user.role || 'default'}`}>
          {user.role || 'admin'}
        </span>
        <button
          type="button"
          className="btn btn--danger"
          disabled={busy}
          onClick={remove}
        >
          <RemoveIcon /> Remove
        </button>
      </div>
      {err && (
        <p style={{ color: 'var(--danger-700)', fontSize: 12, marginTop: 6 }}>{err}</p>
      )}
      {confirmNode}
    </li>
  );
}

function InheritField({ label, fallback, value, onChange, placeholder }) {
  return (
    <label className="field">
      <span className="field__label">
        {label}
        {fallback && !value && (
          <span style={{ color: 'var(--text-3)', fontWeight: 400, marginLeft: 6, textTransform: 'none', letterSpacing: 0 }}>
            inherits <code>{fallback}</code>
          </span>
        )}
      </span>
      <input
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}
